import { useContext, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useMarkDocketHasPrinted } from "../../../hooks/api/mutations/useSection";
import { useReactToPrint } from "react-to-print";
import { useGetSectionItemDetails } from "../../../hooks/api/queries/useSection";
import { cleanObject } from "../../../helpers";
import { RequestLoader } from "../../../hooks/context";
import { useCloseOtpAuthorizationPrompt } from "../../../hooks/useCloseOtpAuthorizationPrompt";

const useSectionView = ({
  section_id,
  allowPrint = false,
  setAllowPrint,
  isDocket = false,
  invalidateQueries = () => {},
}) => {
  // Detect if the device is mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const sectionPrintComponent = useRef();
  const queryClient = useQueryClient();
  const { closeOtpAuthorizationPrompt } = useCloseOtpAuthorizationPrompt();
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const { business_name, address } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  const { data: sectionDetails, isLoading: isFetchingSectionItem } =
    useGetSectionItemDetails(
      cleanObject({
        id: section_id,
        enabled: allowPrint,
        onSuccess: () => {
          handleMarkDocketHasPrinted();
          closeOtpAuthorizationPrompt();
        },
        onSettled: () => setRequestLoaderProgress(100),
      }),
    );

  const { handleMarkDocketHasPrinted } = useMarkDocketHasPrinted({
    id: section_id,
    order_id: sectionDetails?.order?.id,
  });

  const handlePrint = useReactToPrint({
    content: () => sectionPrintComponent.current,
    onAfterPrint: () => clearAfterPrint(),
    removeAfterPrint: true,
  });

  useEffect(() => {
    if (allowPrint && sectionDetails && !isMobile) handlePrint();
  }, [allowPrint, sectionDetails]);

  const clearAfterPrint = () => {
    if (isDocket && !sectionDetails?.is_printed) {
      queryClient.invalidateQueries(["getOrderList"]);
      invalidateQueries();
      handleMarkDocketHasPrinted();
    }
    closeOtpAuthorizationPrompt();
    setAllowPrint(false);
  };

  return {
    isMobile,
    business_name,
    address,
    sectionDetails,
    sectionPrintComponent,
    allowPrint,
    isFetchingSectionItem,
    clearAfterPrint,
  };
};

export default useSectionView;
